import React, { useState } from 'react';

import { Button, Flex, Group, Modal, Stack, Text } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SyncFrequencySelect } from 'components/SyncFrequencySelect';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { RESOURCES_QUERY_KEY } from 'shared/constants/query-keys';
import { Resource, SyncFrequency } from 'shared/interfaces/Resource';
import { convertMsToSyncFrequency, syncFrequencyOptions } from 'shared/utils/sync';

interface SyncFrequencyEditModalProps {
  resource: Resource;
  opened: boolean;
  closeSyncFrequencyModal: () => void;
  selectedTeamFilter: string;
  couldSync?: boolean;
}
export function SyncFrequencyEditModal({
  resource,
  opened,
  closeSyncFrequencyModal,
  selectedTeamFilter,
  couldSync = false
}: SyncFrequencyEditModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedFrequency, setSelectedFrequency] = useState<SyncFrequency>(
    resource.syncFrequencyMs ? convertMsToSyncFrequency(resource.syncFrequencyMs) : SyncFrequency.NEVER
  );

  const updateSyncFrequecyMutation = useMutation({
    mutationFn: ResourceService.updateResourceInfo,
    onSuccess: (updatedResource: Resource) => {
      queryClient.setQueryData([RESOURCES_QUERY_KEY, selectedTeamFilter], (existingResources: Resource[] = []) =>
        existingResources.map((resource) => (resource._id === updatedResource._id ? updatedResource : resource))
      );
      NotificationService.showResult(true, 'FEED_BACK.SYNC.SUCCESS.UPDATE');
      closeSyncFrequencyModal();
    },
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.SYNC.FAILURE.UPDATE');
    }
  });

  const handleSave = () => {
    updateSyncFrequecyMutation.mutate({
      resourceId: resource._id,
      syncFrequency: selectedFrequency
    });
  };

  const handleCancel = () => {
    setSelectedFrequency(
      resource.syncFrequencyMs ? convertMsToSyncFrequency(resource.syncFrequencyMs) : SyncFrequency.NEVER
    );
    closeSyncFrequencyModal();
  };

  return (
    <Modal
      size="md"
      title={
        <Flex align="center" gap="sm">
          <IconClock size={24} />
          <Text size="lg">{t('RESOURCE_TILE.SYNC.CONFIGURE_SYNC_FREQUENCY')}</Text>
        </Flex>
      }
      opened={opened}
      onClose={closeSyncFrequencyModal}>
      <Stack>
        <Text color="dimmed">{t('RESOURCE_TILE.SYNC.SYNC_FREQUENCY_DESCRIPTION')}</Text>

        <SyncFrequencySelect
          defaultSyncFrequency={selectedFrequency}
          onChange={(value) => setSelectedFrequency(value as SyncFrequency)}
          couldSync={couldSync}
          resourceType={resource.type}
          data={syncFrequencyOptions}
        />

        <Group position="right" mt="md">
          <Button variant="default" onClick={handleCancel}>
            {t('APP.ACTIONS.CANCEL')}
          </Button>
          <Button onClick={handleSave} loading={updateSyncFrequecyMutation.isLoading}>
            {t('APP.ACTIONS.SAVE_CHANGES')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
